import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';

import { Typography } from '@/components/ui/typography';

interface TabProps {
  Icon?: React.ComponentType<{ className?: string }>;
  title: string;
  to: string;
  isActive?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  /**
   * Carry the current URL search string onto the new tab. Use for tabs that
   * sit inside a page whose state is encoded in query params (e.g. the
   * opening-details `?hiring_goal_id=…`) so switching tabs doesn't reset it.
   */
  preserveSearch?: boolean;
}

export function Tab({
  Icon,
  title,
  to,
  isActive = false,
  onClick,
  preserveSearch = false,
}: TabProps) {
  const location = useLocation();
  const linkTo = preserveSearch
    ? { pathname: to, search: location.search }
    : to;

  return (
    <Link
      to={linkTo}
      onClick={onClick}
      className={clsx(
        'group flex h-10 items-center justify-center gap-1 border-b-2 px-3 py-2',
        'hover:bg-fx-brand-50 hover:border-fx-brand-100',
        { 'border-fx-primary': isActive },
        { 'border-transparent': !isActive },
      )}
    >
      {Icon && (
        <Icon
          className={clsx(
            isActive ? 'text-fx-primary' : 'text-fx-neutral-400',
            'group-hover:text-fx-primary',
          )}
        />
      )}
      <Typography
        variant="text"
        className={clsx(
          !isActive && '!text-fx-neutral-600',
          'group-hover:!text-fx-primary',
        )}
      >
        {title}
      </Typography>
    </Link>
  );
}
